/**
 * ManageNamespaceHeader
 *
 * Header for the manage namespaces page with edit toggle and create button.
 * Also displays an info alert when edit mode is active or admin rights absent.
 */
import React from 'react';
import { Plus, Edit2, Shield, Info } from 'lucide-react';

export default function ManageNamespaceHeader({ 
  onNewNamespace, 
  hasNamespaces, 
  isEditMode, 
  onToggleEditMode,
  hasAdminNamespaces
}) {
  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Manage Namespaces
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
            Namespaces allow you to organize your content and control access for different teams or projects. 
            Each namespace can have its own members with different permission levels.
          </p>
          
          {isEditMode && (
            <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/30 border border-purple-100 dark:border-purple-800 rounded-md text-sm text-purple-600 dark:text-purple-300 flex items-center">
              <Info className="h-5 w-5 mr-2 flex-shrink-0 text-purple-500 dark:text-purple-400" />
              <span>Note: You cannot delete a namespace that is currently active. Switch to a different namespace first.</span>
            </div>
          )}
        </div>
        <div className="mt-4 md:mt-0 md:ml-4 flex-shrink-0 flex space-x-3">
          {hasAdminNamespaces && (
            isEditMode ? (
              <button
                onClick={onToggleEditMode}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
              >
                Done
              </button>
            ) : (
              <button
                onClick={onToggleEditMode}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Edit Namespaces
              </button>
            )
          )}
          <button
            onClick={onNewNamespace}
            className="inline-flex items-center justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Namespace
          </button>
        </div>
      </div>
      {!hasAdminNamespaces && hasNamespaces && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900/50 dark:bg-amber-900/30">
          <div className="flex items-start">
            <Shield className="h-5 w-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0 dark:text-amber-500" />
            <p className="text-amber-800 dark:text-amber-400">
              Only admins can edit or delete namespaces. You currently don't have admin access to any namespaces.
            </p>
          </div>
        </div>
      )}
    </div>
  );
} 